import { PublicClientApplication, type Configuration } from '@azure/msal-browser';

/**
 * MSAL configuration + token-acquisition helpers.
 *
 * Modes (mirrors the server):
 *   - "real":   VITE_ENTRA_TENANT_ID + VITE_ENTRA_CLIENT_ID + VITE_ENTRA_API_SCOPE
 *               must all be set. Real sign-in flow is enforced.
 *   - "bypass": Any missing env var, OR VITE_AUTH_MODE=bypass. The app skips
 *               MSAL entirely; `acquireToken()` returns null and the API client
 *               sends requests without an Authorization header. The server's
 *               matching bypass injects a dev user.
 *
 * The bypass exists so we can develop locally without a real Entra app
 * registration. Flip env vars to switch real auth on; no code changes needed.
 */

const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID;
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID;
const API_SCOPE = import.meta.env.VITE_ENTRA_API_SCOPE;
const FORCE_BYPASS = import.meta.env.VITE_AUTH_MODE === 'bypass';

export const AUTH_BYPASSED =
  FORCE_BYPASS || !TENANT_ID || !CLIENT_ID || !API_SCOPE;

const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID ?? '',
    authority: TENANT_ID
      ? `https://login.microsoftonline.com/${TENANT_ID}`
      : 'https://login.microsoftonline.com/common',
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
};

export const msalInstance = AUTH_BYPASSED
  ? null
  : new PublicClientApplication(msalConfig);

/** Scopes requested for our API. */
export const apiScopes: string[] = API_SCOPE ? [API_SCOPE] : [];

/**
 * Initialize MSAL. Must be called before MsalProvider renders. Returns
 * immediately in bypass mode.
 */
export async function initMsal(): Promise<void> {
  if (!msalInstance) return;
  await msalInstance.initialize();
  // Handle the redirect response when returning from the Entra login page.
  const result = await msalInstance.handleRedirectPromise();
  if (result?.account) {
    msalInstance.setActiveAccount(result.account);
  } else {
    // Re-hydrate active account from cache on page reload
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0]!);
    }
  }
}

/**
 * Acquire an access token for our API. Tries silent first (cached); falls
 * back to interactive (full-page redirect) only when the silent attempt fails
 * (e.g., refresh token expired). Returns null in bypass mode.
 */
export async function acquireToken(): Promise<string | null> {
  if (!msalInstance) return null;
  const account = msalInstance.getActiveAccount();
  if (!account) {
    await msalInstance.loginRedirect({ scopes: apiScopes });
    // loginRedirect navigates away; this line is effectively unreachable.
    return null;
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: apiScopes,
      account,
    });
    return result.accessToken;
  } catch {
    // Silent failed (interaction required) — trigger redirect flow
    await msalInstance.acquireTokenRedirect({ scopes: apiScopes });
    return null;
  }
}

/** Get the signed-in user's display info, if any. */
export function getActiveUser():
  | { name?: string; email?: string; oid?: string }
  | null {
  if (!msalInstance) return null;
  const acct = msalInstance.getActiveAccount();
  if (!acct) return null;
  return {
    name: acct.name,
    email: acct.username,
    oid: acct.localAccountId,
  };
}

export async function signOut(): Promise<void> {
  if (!msalInstance) return;
  await msalInstance.logoutRedirect();
}
